import { useCallback, useEffect, useMemo, useState } from 'react'
import { HandCoins, Loader2 } from 'lucide-react'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportTipReport,
  fetchTipReport,
  formatReportDate,
  formatReportMoney,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'

export function TipReportPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
    }),
    [period, customFrom, customTo, bounds, compare],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchTipReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load tips report.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportTipReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <HandCoins className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Tips</h2>
            <p className="text-sm text-muted-foreground">
              Direct and pool tips recorded on POS sales — grouped by stylist and line detail.
            </p>
          </div>
        </div>
        <ReportFilterBar
          period={period}
          compare={compare}
          customFrom={customFrom}
          customTo={customTo}
          onPeriodChange={setPeriod}
          onCompareChange={setCompare}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          onExportCsv={() => handleExport('csv')}
          onExportPdf={() => handleExport('pdf')}
          exporting={exporting}
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <SummaryCard label="Tips" value={String(report.summary.tip_count)} />
            <SummaryCard
              label="Total"
              value={`AED ${formatReportMoney(report.summary.total_amount)}`}
            />
            <SummaryCard
              label="Direct"
              value={`AED ${formatReportMoney(report.summary.direct_amount)}`}
            />
            <SummaryCard
              label="Pool"
              value={`AED ${formatReportMoney(report.summary.pool_amount)}`}
            />
            <SummaryCard
              label="Pending payout"
              value={`AED ${formatReportMoney(report.summary.pending_amount)}`}
            />
          </div>

          {report.compare_summary ? (
            <p className="mb-4 text-sm text-muted-foreground">
              Compare period tips: AED {formatReportMoney(report.compare_summary.total_amount)}
              {' · '}
              {report.compare_summary.tip_count} entries
            </p>
          ) : null}

          <div className="mb-6 overflow-x-auto rounded-2xl border border-border/70">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Stylist</th>
                  <th className="px-4 py-3 font-medium">Tips</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {report.by_stylist.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                      No direct tips in this period.
                    </td>
                  </tr>
                ) : (
                  report.by_stylist.map((row) => (
                    <tr key={row.stylist_id ?? row.stylist_name} className="border-t border-border/60">
                      <td className="px-4 py-3 font-medium">{row.stylist_name ?? '—'}</td>
                      <td className="px-4 py-3">{row.tip_count}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.tip_total)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Tip entries
          </h3>
          <div className="overflow-x-auto rounded-2xl border border-border/70">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Stylist</th>
                  <th className="px-4 py-3 font-medium">Sale</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {report.tips.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No tip entries in this period.
                    </td>
                  </tr>
                ) : (
                  report.tips.map((row) => (
                    <tr key={row.id} className="border-t border-border/60">
                      <td className="px-4 py-3">{formatReportDate(row.recorded_at)}</td>
                      <td className="px-4 py-3 capitalize">{row.tip_type}</td>
                      <td className="px-4 py-3">{row.stylist_name ?? '—'}</td>
                      <td className="px-4 py-3">{row.sale_number ?? '—'}</td>
                      <td className="px-4 py-3">AED {formatReportMoney(row.amount)}</td>
                      <td className="px-4 py-3 capitalize">{row.status}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
